import React, { useEffect } from "react";
import { Field, useFormikContext } from "formik";
import { useGetAvailableRidersQuery } from "../../../../services/redux/api/components";

const RidersTable = ({ insurerId }) => {
    // Determine insurer id from props (support either camelCase or PascalCase)
    const resolvedInsurerId = insurerId ?? (typeof insurerId === 'undefined' ? undefined : insurerId);
    const skip = !resolvedInsurerId;
    const payload = { insurerId: resolvedInsurerId };

    // Call API explicitly with payload; use skip option when not available
    const { data, isLoading, isError } = useGetAvailableRidersQuery(payload, { skip });
    const { setFieldValue, values } = useFormikContext();

    useEffect(() => {
        console.log("RidersTable payload/skip:", payload, "skip:", skip);
        console.log("RidersTable data:", data);
        console.log("Formik riders before mapping:", values?.riders);

        if (!skip && Array.isArray(data?.data)) {
            const apiRiders = data.data;
            const existing = Array.isArray(values?.riders) ? values.riders : [];

            // Merge: keep existing entries' sum/term/ppt/premium when present (editing case),
            // but ensure the array contains at least the available riders (by RiderId or name).
            const mapped = apiRiders.map((r) => {
                // Try to find matching existing rider by RiderId or RiderName
                const existingMatch = existing.find(
                    (ex) => (ex.RiderId && r.RiderId && ex.RiderId === r.RiderId) ||
                            (ex.RiderName && (ex.RiderName === r.RiderName || ex.RiderName === r.Rider_Name))
                );

                return {
                    RiderId: r.RiderId ?? null,
                    RiderName: r.RiderName ?? r.Rider_Name ?? r.name ?? "",
                    // prefer existing filled values when editing
                    sum: existingMatch?.sum ?? existingMatch?.SUM ?? r.SUM ?? r.sum ?? "",
                    term: existingMatch?.term ?? existingMatch?.Term ?? r.Term ?? r.term ?? "",
                    ppt: existingMatch?.ppt ?? existingMatch?.PPT ?? r.PPT ?? r.ppt ?? "",
                    premium: existingMatch?.premium ?? existingMatch?.Premium ?? r.Premium ?? r.premium ?? "",
                };
            });

            // If there are extra existing riders (custom entries) that are not in apiRiders,
            // keep them appended so user data isn't lost.
            const extras = existing.filter((ex) => {
                if (!ex) return false;
                // treat entries without RiderId but with RiderName as extras
                const found = apiRiders.find((ar) => (ar.RiderId && ex.RiderId && ar.RiderId === ex.RiderId) || (ex.RiderName && (ex.RiderName === ar.RiderName || ex.RiderName === ar.Rider_Name)));
                return !found;
            });

            const finalList = [...mapped, ...extras];
            console.log("Setting merged Formik riders:", finalList);
            setFieldValue("riders", finalList);
        } else if (skip && values?.riders?.length) {
            setFieldValue("riders", []);
        }
    }, [data, skip, setFieldValue, values?.riders?.length]);

    if (isLoading) {
        return (
            <tr>
                <td colSpan={5} className="text-center">
                    Loading riders...
                </td>
            </tr>
        );
    }

    if (isError || !data?.success) {
        return (
            <tr>
                <td colSpan={5} className="text-center text-danger">
                    Failed to load riders
                </td>
            </tr>
        );
    }

    const displayList =
        values?.riders && values.riders.length > 0
            ? values.riders
            : Array.isArray(data?.data)
            ? data.data
            : [];

    console.log("RidersTable displayList:", displayList);

    return (
        <>
            {displayList.length > 0 ? (
                displayList.map((rider, idx) => (
                    <tr key={rider.RiderId || idx}>
                        <td>{rider.RiderName}</td>
                        <td>
                            <Field
                                name={`riders[${idx}].sum`}
                                type="number"
                                className="form-control"
                                placeholder="Enter Sum"
                            />
                        </td>
                        <td>
                            <Field
                                name={`riders[${idx}].term`}
                                type="number"
                                className="form-control"
                                placeholder="Enter Term"
                            />
                        </td>
                        <td>
                            <Field
                                name={`riders[${idx}].ppt`}
                                type="number"
                                className="form-control"
                                placeholder="Enter PPT"
                            />
                        </td>
                        <td>
                            <Field
                                name={`riders[${idx}].premium`}
                                type="number"
                                className="form-control"
                                placeholder="Enter Premium"
                            />
                        </td>
                    </tr>
                ))
            ) : (
                <tr>
                    <td colSpan={5} className="text-center text-muted">
                        No riders available
                    </td>
                </tr>
            )}
        </>
    );
};

const PremiumInfoStep = ({ insurerId }) => {
    console.log("Rendering PremiumInfoStep with insurerId:", insurerId);

    return (
        <>
            <h6 className="text-brandRed border-bottom pb-2 mb-4">
                Premium Information
            </h6>

            {/* 🔹 Premium & Payment Fields */}
            <div className="row g-3 mb-4">
                <div className="col-md-4">
                    <label className="form-label">Payment Mode</label>
                    <Field as="select" name="PaymentMode" className="form-select">
                        <option value="">Select Payment Mode</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                        <option value="Half-Yearly">Half-Yearly</option>
                        <option value="Yearly">Yearly</option>
                    </Field>
                </div>

                <div className="col-md-4">
                    <label className="form-label">Basic Premium</label>
                    <Field name="BasicPremium" type="number" className="form-control" />
                </div>

                <div className="col-md-4">
                    <label className="form-label">Extra Premium</label>
                    <Field name="ExtraPremium" type="number" className="form-control" />
                </div>

                <div className="col-md-4">
                    <label className="form-label">GST 1st Year</label>
                    <Field name="GSTFirstYear" type="number" className="form-control" />
                </div>

                <div className="col-md-4">
                    <label className="form-label">GST 2nd Year to Term</label>
                    <Field
                        name="GSTSecondYearOnwards"
                        type="number"
                        className="form-control"
                    />
                </div>

                <div className="col-md-4">
                    <label className="form-label">Tax Beneficiary</label>
                    <Field
                        name="TaxBeneficiary"
                        type="text"
                        className="form-control"
                        placeholder="Enter Tax Beneficiary"
                    />
                </div>
            </div>

            {/* 🔹 Riders Table */}
            <div className="table-responsive mt-3 pt-2">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Rider Name</th>
                            <th>SUM</th>
                            <th>Term</th>
                            <th>PPT</th>
                            <th>Premium</th>
                        </tr>
                    </thead>
                    <tbody>
                        {insurerId ? (
                            <RidersTable insurerId={insurerId} />
                        ) : (
                            <tr>
                                <td colSpan={5} className="text-center text-muted">
                                    Select Insurer to see riders
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default PremiumInfoStep;
